import {FloatingText} from '../../game_ui_view_objects/FloatingText'
import {PIXI} from '@colonist/pixi'
import {UIGameManager} from '../UIGameManager'
import {UIGameMechanic} from '../UIGameEventController'
import {GameStateEnums} from '../../../../../shared/GameEnums'
import {BeginnerHintFormat, GameAction} from '@colonist/game-base-types'
import {UIGameActionButtonView} from './UIGameActionButtonView'
import {KeyBindController, ShortcutLocation} from '../../KeyBindController'
import {UIGameActionButtonPositions} from './UIGameActionsController'
import {UIGameBeginnerHintView} from '../ui_game_beginner_hints/UIGameBeginnerHintView'
import {bringToFront} from '../../ui_utils/PixiUtils'
import {Sprites} from '../UIGameImageDictionary'
import {UIGameSpriteHandler} from '../../UIGameSpriteHandler'
import {Sound} from '../../Sound'
import {BeginnerModeActionState} from '@colonist/game-state-types'
import {HiddenUIElement} from '@colonist/game-beginner-mode'
import {ActionState, TurnState} from '@colonist/game-states'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums
import {UIUtilsEjsVariables} from '../../UIUtilsEjsVariables'
import {SUGGEST_ACTION_ANIMATION_DURATION, UIGameExpandContractAnimation} from '../../ui_canvas_engine/animation/UIGameExpandContractAnimation'

export class UIGameActionButtonViewTurn extends UIGameActionButtonView implements UIGameMechanic {
    private endTurnEnabled: boolean
    private actionSuggestionAnimation: UIGameExpandContractAnimation
    private diceRolledTime: number
    private readonly milliSecondsSpacebarShortcutInactiveAtTurnStart: number = 500

    constructor(uiGameManager: UIGameManager, container: PIXI.Container) {
        const texture = UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.icon_hourglass)
        super(uiGameManager, UIGameActionButtonPositions.EndTurn, container)
        this.addCenteredIcon(texture)

        if(!UIUtilsEjsVariables.isUserOnMobile) {
            this.setTip({key: 'strings:game.tooltips.endTurn'}, [{key: 'strings:colonistCommon.utils.emptyString'}])
        }

        this.setUpdateAction()
        this.stateUpdated()
        this.repositionedButton = () => {
            const beginnerHintActive = this.beginnerHintView != undefined
            this.destroyBeginnerHintView()
            if(beginnerHintActive) this.beginnerHintReceived({type: GameAction.PassedTurn, data: {}})
        }
    }

    buttonSpecificEvents(data: any, eventType: UIGameEventEnums) {
        switch(eventType) {
            case UIGameEventEnums.CloseTradeUI:
            case UIGameEventEnums.PlayerControllerStateUpdated:
            case UIGameEventEnums.GameStateUpdated: this.stateUpdated(); break
            case UIGameEventEnums.BeginnerHintReceived: this.beginnerHintReceived(data); break
            case UIGameEventEnums.BeginnerModeActionUpdated: this.onBeginnerModeActionUpdate(data); break
            case UIGameEventEnums.BeginnerModeInteractWithGuide: this.onBeginnerModeInteractWithGuide(data, GameAction.PassedTurn); break
            case UIGameEventEnums.SuggestAction: this.onSuggestAction(data); break
            case UIGameEventEnums.DiceStateUpdated: this.setDiceRolledTime(); break
            default: break
        }
    }

    private setUpdateAction() {
        this.updateAction(() => {
            this.clickedTurnButton()
        })
        KeyBindController.bindKey('Space', () => this.clickedTurnButton(), () => this.isListeningForKeyPress(), ShortcutLocation.InGame)
    }

    private isListeningForKeyPress(): boolean {
        if(this.uiGameManager.pauseController.isPaused()) return false
        if(!this.uiGameManager.gameController.isMyTurn) return false
        if(!this.endTurnEnabled) return false
        if(Date.now() - this.diceRolledTime < this.milliSecondsSpacebarShortcutInactiveAtTurnStart) return false
        return true
    }

    private clickedTurnButton() {
        if(!this.uiGameManager.gameController.isMyTurn) {
            new FloatingText({key: 'strings:game.errors.notYourTurn'}, this)
            return
        }
        if(this.uiGameManager.gameController.currentState.actionState != ActionState.None) {
            new FloatingText({key: 'strings:game.errors.finishCurrentAction'}, this)
            return
        }
        this.uiGameManager.socketGameSend.clickedPassTurn()
    }

    private stateUpdated() {
        this.deactivateEndTurn()
        this.updateButtonForTurnState()
    }

    private setDiceRolledTime() {
        if(this.uiGameManager.gameController.diceState.diceThrown) {
            this.diceRolledTime = Date.now()
        }
    }

    private updateButtonForTurnState() {
        if(!this.allowableTurnStates()) return
        if(!this.uiGameManager.gameController.isMyTurn) return
        if(this.uiGameManager.gameController.currentState.actionState != ActionState.None) return
        this.activateEndTurn()
    }

    private allowableTurnStates(): boolean {
        if(this.uiGameManager.gameController.currentState.turnState == TurnState.Turn) return true
        if(this.uiGameManager.gameController.currentState.turnState == TurnState.SpecialBuildPhase) return true

        return false
    }

    private activateEndTurn() {
        this.updateTexture(UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.icon_pass_turn))
        this.activateOpacity()
        this.endTurnEnabled = true
    }

    private deactivateEndTurn() {
        this.updateTexture(UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.icon_hourglass))
        this.inactivateOpacity()
        this.endTurnEnabled = false
        this.actionSuggestionAnimation?.stop()
    }

    private beginnerHintReceived(data: BeginnerHintFormat) {
        if(this.parent == undefined) return
        if(this.visible == false) return
        if(data.type != GameAction.PassedTurn) return

        this.destroyBeginnerHintView()
        Sound.playSound(Sound.SoundType.BeginnerHintActivated)
        this.beginnerHintView = new UIGameBeginnerHintView(this.uiGameManager, this, {key: 'strings:game.beginnerTips.actionHints.description', options: {context: 'endTurn'}}, UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.icon_pass_turn))
        bringToFront(this.parent)
    }

    private onSuggestAction(data: GameAction) {
        if(data != GameAction.PassedTurn) return
        this.actionSuggestionAnimation = new UIGameExpandContractAnimation(this.viewContainer, SUGGEST_ACTION_ANIMATION_DURATION)
        this.actionSuggestionAnimation.start()
        Sound.playSound(Sound.SoundType.BeginnerHintActivated)
    }

    private onBeginnerModeActionUpdate(data: BeginnerModeActionState) {
        this.setShouldRevealButton(data, HiddenUIElement.EndTurnActionButton, GameAction.PassedTurn)

        const isPassedTurnAction = data.type == GameAction.PassedTurn
        isPassedTurnAction ? this.activateOpacity() : this.inactivateOpacity()
        isPassedTurnAction ? this.setUpdateAction() : this.updateAction(() => new FloatingText({key: 'strings:game.errors.finishCurrentAction'}, this))
    }
}
